import { useEffect, useMemo, useRef, useState } from 'react';
import {
  cueHighlights,
  cueIsActive,
  DARK_INK,
  NowYouTry,
  usePrefersReducedMotion,
  useMountSequence,
  VizCard,
  type VizCue,
} from '../vizShared';

// PhScale — interactive pH / pOH explorer (0–14). Drag the slider OR type a
// concentration ([H+] or [OH-]) and watch pH, pOH, [H+], [OH-], the universal-
// indicator color and an example substance update live. A strong/weak toggle
// shows how a weak acid/base of the same formal concentration lands closer to 7.
//
// config: { ph?, title?, mode? ('acid'|'base'), ka?, kb?, conc? }
// Resilient: every field defaults; nothing here can throw on bad input.

export interface PhScaleConfig {
  title?: string;
  ph?: number;
  mode?: 'acid' | 'base';
  ka?: number; // weak-acid dissociation constant (for the weak toggle)
  kb?: number; // weak-base constant
  conc?: number; // formal concentration mol/L used by the weak model
}

interface Props {
  config?: PhScaleConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const KW = 1e-14;

// Universal-indicator color stops across the 0–14 range (classic chart hues).
const INDICATOR_STOPS: Array<{ ph: number; color: string }> = [
  { ph: 0, color: '#C0263B' },
  { ph: 2, color: '#E0563B' },
  { ph: 4, color: '#E8923C' },
  { ph: 5, color: '#E8C53C' },
  { ph: 6, color: '#C7D63C' },
  { ph: 7, color: '#5FB85F' },
  { ph: 8, color: '#3CB89B' },
  { ph: 9, color: '#3C9BD6' },
  { ph: 11, color: '#3C5BD6' },
  { ph: 13, color: '#6A3CC7' },
  { ph: 14, color: '#4A1C8C' },
];

// Familiar reference substances by approximate pH (closest one is highlighted).
const SUBSTANCES: Array<{ ph: number; name: string; emoji: string }> = [
  { ph: 0, name: 'Battery acid', emoji: '🔋' },
  { ph: 1.5, name: 'Stomach acid', emoji: '🌡️' },
  { ph: 2.2, name: 'Lemon juice', emoji: '🍋' },
  { ph: 2.9, name: 'Vinegar', emoji: '🧴' },
  { ph: 3.5, name: 'Orange juice', emoji: '🍊' },
  { ph: 4.5, name: 'Tomato', emoji: '🍅' },
  { ph: 5.5, name: 'Black coffee', emoji: '☕' },
  { ph: 6.5, name: 'Milk', emoji: '🥛' },
  { ph: 7, name: 'Pure water', emoji: '💧' },
  { ph: 7.4, name: 'Blood', emoji: '🩸' },
  { ph: 8.3, name: 'Sea water', emoji: '🌊' },
  { ph: 9, name: 'Baking soda', emoji: '🧁' },
  { ph: 10.5, name: 'Milk of magnesia', emoji: '🧪' },
  { ph: 11.5, name: 'Ammonia', emoji: '🧹' },
  { ph: 12.5, name: 'Bleach', emoji: '🧽' },
  { ph: 14, name: 'Drain cleaner', emoji: '🚿' },
];

function clampPh(p: number) {
  if (!Number.isFinite(p)) return 7;
  return Math.min(14, Math.max(0, p));
}

function lerpColor(a: string, b: string, t: number) {
  const pa = [parseInt(a.slice(1, 3), 16), parseInt(a.slice(3, 5), 16), parseInt(a.slice(5, 7), 16)];
  const pb = [parseInt(b.slice(1, 3), 16), parseInt(b.slice(3, 5), 16), parseInt(b.slice(5, 7), 16)];
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * t));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

function colorForPh(ph: number) {
  const p = clampPh(ph);
  for (let i = 0; i < INDICATOR_STOPS.length - 1; i++) {
    const lo = INDICATOR_STOPS[i];
    const hi = INDICATOR_STOPS[i + 1];
    if (p >= lo.ph && p <= hi.ph) {
      const t = (p - lo.ph) / (hi.ph - lo.ph || 1);
      return lerpColor(lo.color, hi.color, t);
    }
  }
  return INDICATOR_STOPS[INDICATOR_STOPS.length - 1].color;
}

function closestSubstance(ph: number) {
  return SUBSTANCES.reduce((best, s) =>
    Math.abs(s.ph - ph) < Math.abs(best.ph - ph) ? s : best,
  );
}

// Format a concentration in scientific notation for readability.
function sci(x: number) {
  if (!Number.isFinite(x) || x <= 0) return '0';
  const exp = Math.floor(Math.log10(x));
  const mant = x / 10 ** exp;
  return `${mant.toFixed(2)} × 10${supExp(exp)}`;
}
const SUPS: Record<string, string> = { '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' };
function supExp(n: number) {
  return String(n).split('').map((ch) => SUPS[ch] ?? ch).join('');
}

const W = 520;

export default function PhScale({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const initialPh = clampPh(config.ph ?? 3);
  const [ph, setPh] = useState<number>(reduced || hasCue ? initialPh : 0);
  const [weak, setWeak] = useState(false);
  // Inline feedback when a typed concentration can't be parsed (instead of a
  // silent no-op that leaves the student wondering why nothing happened).
  const [concError, setConcError] = useState<string | null>(null);
  const interactedRef = useRef(false);
  const mode: 'acid' | 'base' = config.mode ?? (ph < 7 ? 'acid' : 'base');

  const mark = () => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'ph_scale' });
    }
  };

  // ---- Self-demo: sweep the indicator across the scale, then settle on config.
  const demo = useMountSequence(
    [
      { at: 200, run: () => setPh(0) },
      { at: 500, run: () => setPh(3.5) },
      { at: 850, run: () => setPh(7) },
      { at: 1200, run: () => setPh(11) },
      { at: 1550, run: () => setPh(14) },
      { at: 1950, run: () => setPh(initialPh) },
    ],
    { skip: reduced || hasCue, onSettle: () => setPh(initialPh) },
  );
  const cancelDemo = () => { if (demo.running) demo.cancel(); };

  // ---- Cue: progress → pH = progress*14; highlight → named sample; step → samples.
  useEffect(() => {
    if (cue?.progress == null) return;
    setWeak(false);
    setPh(clampPh(Math.max(0, Math.min(1, cue.progress)) * 14));
  }, [cue?.progress]);

  useEffect(() => {
    const hi = cueHighlights(cue);
    if (hi.length === 0) return;
    const s = SUBSTANCES.find((sub) => hi.some((h) => sub.name.toLowerCase().includes(h)));
    if (s) { setWeak(false); setPh(clampPh(s.ph)); }
  }, [cue]);

  useEffect(() => {
    if (cue?.step == null) return;
    const idx = Math.max(0, Math.min(SUBSTANCES.length - 1, Math.round(cue.step)));
    setWeak(false);
    setPh(clampPh(SUBSTANCES[idx].ph));
  }, [cue?.step]);

  // The "weak" version recomputes pH from a Ka/Kb + concentration model so the
  // student sees that a weak acid barely dissociates and sits nearer neutral.
  const weakPh = useMemo(() => {
    const c = config.conc ?? 0.1;
    if (mode === 'acid') {
      const ka = config.ka ?? 1.8e-5; // acetic acid default
      const h = Math.sqrt(ka * c);
      return clampPh(-Math.log10(h));
    }
    const kb = config.kb ?? 1.8e-5; // ammonia default
    const oh = Math.sqrt(kb * c);
    return clampPh(14 + Math.log10(oh));
  }, [config.conc, config.ka, config.kb, mode]);

  const effectivePh = weak ? weakPh : ph;
  const h = 10 ** -effectivePh;
  const oh = KW / h;
  const poh = 14 - effectivePh;
  const color = colorForPh(effectivePh);
  const sub = closestSubstance(effectivePh);
  const nature = effectivePh < 6.5 ? 'Acidic' : effectivePh > 7.5 ? 'Basic' : 'Neutral';

  const setFromConc = (raw: string, kind: 'h' | 'oh') => {
    cancelDemo();
    const trimmed = raw.trim();
    if (trimmed === '') {
      setConcError(null);
      return;
    }
    const v = parseFloat(trimmed);
    if (!Number.isFinite(v) || v <= 0) {
      setConcError('Enter a positive concentration, e.g. 1e-3 or 0.001.');
      return;
    }
    setConcError(null);
    const newPh = kind === 'h' ? -Math.log10(v) : 14 + Math.log10(v);
    setPh(clampPh(newPh));
    setWeak(false);
    mark();
  };

  const markerTransition = reduced ? undefined : 'all 320ms ease-out';

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Acid–base scale"
      title={config.title ?? 'pH / pOH scale'}
      subtitle="Universal-indicator color, [H⁺], [OH⁻] and a familiar reference substance"
      hint="Drag the slider or type a concentration · ←/→ to adjust pH · sweeps a demo on load"
      actions={
        <button
          type="button"
          onClick={() => { cancelDemo(); setConcError(null); setWeak((w) => !w); mark(); }}
          className={`rounded-sm border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent focus-visible:ring-offset-1 ${
            weak ? 'border-ws-accent bg-ws-accent text-ws-on-accent' : 'border-ws-border bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
          }`}
          aria-pressed={weak}
        >
          {weak ? `Weak ${mode} model` : `Strong (manual)`}
        </button>
      }
    >
      <div className="relative w-full select-none">
        <NowYouTry show={demo.done && !reduced && !hasCue} />

        {/* Gradient bar + marker */}
        <svg viewBox={`0 0 ${W} 92`} className="w-full" role="img" aria-label="pH gradient scale">
          <defs>
            <linearGradient id="phGrad" x1="0" y1="0" x2="1" y2="0">
              {INDICATOR_STOPS.map((s) => (
                <stop key={s.ph} offset={`${(s.ph / 14) * 100}%`} stopColor={s.color} />
              ))}
            </linearGradient>
          </defs>
          <rect x="10" y="14" width={W - 20} height="26" rx="6" fill="url(#phGrad)" />
          {/* tick labels 0..14 */}
          {Array.from({ length: 15 }, (_, i) => i).map((i) => (
            <g key={i}>
              <line x1={10 + ((W - 20) * i) / 14} y1="40" x2={10 + ((W - 20) * i) / 14} y2="46" stroke="var(--ws-ink-4)" strokeWidth="1" />
              <text x={10 + ((W - 20) * i) / 14} y="58" textAnchor="middle" fontSize="9" fill="var(--ws-ink-3)">{i}</text>
            </g>
          ))}
          {/* marker */}
          {(() => {
            const mx = 10 + ((W - 20) * clampPh(effectivePh)) / 14;
            return (
              <g style={{ transition: markerTransition }} transform={`translate(${mx}, 0)`}>
                <line x1={0} y1="10" x2={0} y2="44" stroke="var(--ws-ink)" strokeWidth="2" />
                <polygon points={`-6,8 6,8 0,16`} fill="var(--ws-ink)" />
                <circle cx={0} cy="76" r="11" fill={color} stroke="var(--ws-ink)" strokeWidth="1.5" />
                <text x={0} y="80" textAnchor="middle" fontSize="9" fontWeight="700" fill="#1A1A1A">
                  {effectivePh.toFixed(1)}
                </text>
              </g>
            );
          })()}
        </svg>

        {/* Slider (disabled in weak mode since pH is computed) */}
        <input
          type="range"
          min={0}
          max={14}
          step={0.1}
          value={effectivePh}
          disabled={weak}
          onPointerDown={cancelDemo}
          onKeyDown={cancelDemo}
          onChange={(e) => { setPh(clampPh(parseFloat(e.target.value))); mark(); }}
          className="mt-1 w-full accent-ws-accent disabled:opacity-50"
          aria-label="pH value"
          aria-valuetext={`pH ${effectivePh.toFixed(1)}`}
        />

        {/* Readout grid */}
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="pH" value={effectivePh.toFixed(2)} accent={color} />
          <Stat label="pOH" value={poh.toFixed(2)} />
          <Stat label="[H⁺] mol/L" value={sci(h)} />
          <Stat label="[OH⁻] mol/L" value={sci(oh)} />
        </div>

        {/* Nature + example substance */}
        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-ws-border bg-ws-surface-2 px-3 py-2">
          <span
            className="rounded-sm px-2 py-0.5 text-xs font-semibold"
            style={{ backgroundColor: color, color: DARK_INK, transition: reduced ? undefined : 'background-color 320ms ease-out' }}
          >
            {nature}
          </span>
          <span className="text-2xl leading-none">{sub.emoji}</span>
          <span className="text-sm text-ws-ink-2">
            Near <span className="font-semibold text-ws-ink">{sub.name}</span>{' '}
            <span className="text-ws-ink-3">(~pH {sub.ph})</span>
          </span>
        </div>

        {/* Manual concentration entry */}
        {!weak && (
          <div className="mt-3 flex flex-wrap items-end gap-3 text-xs">
            <label className="flex flex-col gap-1">
              <span className="text-ws-ink-3">Set [H⁺] (mol/L)</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="e.g. 1e-3"
                onBlur={(e) => setFromConc(e.target.value, 'h')}
                onKeyDown={(e) => { if (e.key === 'Enter') setFromConc((e.target as HTMLInputElement).value, 'h'); }}
                onChange={() => { if (concError) setConcError(null); }}
                aria-invalid={concError != null}
                className={`w-28 rounded-sm border bg-ws-tile px-2 py-1 font-mono text-ws-ink focus:outline-none ${
                  concError ? 'border-ws-danger focus:border-ws-danger' : 'border-ws-border focus:border-ws-accent'
                }`}
                aria-label="Hydrogen ion concentration in mol per liter"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-ws-ink-3">Set [OH⁻] (mol/L)</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="e.g. 1e-5"
                onBlur={(e) => setFromConc(e.target.value, 'oh')}
                onKeyDown={(e) => { if (e.key === 'Enter') setFromConc((e.target as HTMLInputElement).value, 'oh'); }}
                onChange={() => { if (concError) setConcError(null); }}
                aria-invalid={concError != null}
                className={`w-28 rounded-sm border bg-ws-tile px-2 py-1 font-mono text-ws-ink focus:outline-none ${
                  concError ? 'border-ws-danger focus:border-ws-danger' : 'border-ws-border focus:border-ws-accent'
                }`}
                aria-label="Hydroxide ion concentration in mol per liter"
              />
            </label>
            <span className="text-ws-ink-3">pH + pOH = 14 · [H⁺][OH⁻] = 1×10⁻¹⁴</span>
            {concError && (
              <p role="alert" className="basis-full text-ws-danger">
                {concError}
              </p>
            )}
          </div>
        )}
        {weak && (
          <p className="mt-3 text-xs text-ws-ink-3">
            Weak {mode} ({config.conc ?? 0.1} M, {mode === 'acid' ? `Ka ${sci(config.ka ?? 1.8e-5)}` : `Kb ${sci(config.kb ?? 1.8e-5)}`}):
            only a small fraction ionizes, so the pH sits well inside the scale rather than at the extreme a strong {mode} of equal concentration would reach.
          </p>
        )}
      </div>
    </VizCard>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="rounded-md border border-ws-border bg-ws-tile px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-ws-ink-3">{label}</div>
      <div className="font-mono text-sm font-semibold tabular-nums" style={{ color: accent ?? 'var(--ws-ink)' }}>
        {value}
      </div>
    </div>
  );
}
